import { useState } from "react";
import { useQuery } from "react-query";
import {
  MdOutlineChevronLeft,
  MdOutlineChevronRight,
  MdOutlineImage,
  MdOutlineLightbulb,
} from "react-icons/md";
import axios from "../config/axiosInstance";
import { SUGGESTIONS } from "../config/apiConfig";
import useDebouncedValue from "../hooks/useDebouncedValue";
import {
  Badge,
  Card,
  EmptyState,
  ErrorBlock,
  SkeletonRows,
} from "../components/crm/Primitives";
import SuggestionDrawer from "../components/crm/SuggestionDrawer";
import { formatDate, formatNumber, PLAN_TONE } from "../components/crm/crmHelpers";
import {
  describeDemand,
  SUGGESTION_AREA_LABELS,
  SUGGESTION_KIND_LABELS,
  SUGGESTION_SORT_OPTIONS,
  SUGGESTION_STATUS_META,
  SUGGESTION_STATUS_ORDER,
} from "../components/crm/suggestionHelpers";

/*
  Caixa de sugestões.

  A lista mostra PEDIDOS: sugestões parecidas são juntadas pela equipe e entram
  como uma linha só, com quantos pedidos e quantas contas pediram. É isso que
  separa "uma cliente quer X" de "12 salões querem Y", e por isso existe a
  ordenação por demanda.

  Os quatro números do topo ignoram os filtros de propósito (o servidor
  calcula só pela carteira): são também os botões que filtram por status.
*/

const PAGE_SIZE = 20;

const SUMMARY_KEY = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
  IMPLEMENTED: "implemented",
};

const PLAN_OPTIONS = ["FREE", "ESSENTIAL", "PRO", "DIAMOND"];

// No celular cada filtro ocupa a própria coluna da grade (`w-full`); de `sm` em
// diante voltam a ter a largura do conteúdo e a quebrar de linha.
const inputClass =
  "w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100 sm:w-auto";

const StatusTile = ({ label, value, tone, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-2xl border bg-white p-4 text-left shadow-sm transition ${
      active
        ? "border-pink-400 ring-2 ring-pink-100"
        : "border-slate-200 hover:border-pink-200"
    }`}
  >
    <Badge tone={tone}>{label}</Badge>
    <p className="mt-2 text-2xl font-bold leading-none text-slate-900">
      {formatNumber(value)}
    </p>
  </button>
);

const SuggestionRow = ({ item, onOpen, isActive }) => {
  const meta = SUGGESTION_STATUS_META[item.status] || SUGGESTION_STATUS_META.PENDING;
  const demand = describeDemand(item.group);
  const demandClass =
    item.group?.requests > 1 ? "font-bold text-slate-800" : "text-slate-400";

  return (
    <button
      type="button"
      onClick={() => onOpen(item.id)}
      className={`flex w-full items-start gap-3 border-b border-slate-100 px-4 py-3 text-left transition last:border-0 ${
        isActive ? "bg-pink-50/60" : "hover:bg-slate-50/80"
      }`}
    >
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-1.5">
          <Badge tone={meta.tone}>{meta.label}</Badge>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            {SUGGESTION_KIND_LABELS[item.kind] || "Sugestão"}
          </span>
          {item.area ? (
            <Badge tone="neutral">
              {SUGGESTION_AREA_LABELS[item.area] || item.area}
            </Badge>
          ) : null}
          {item.photoCount > 0 ? (
            <span className="inline-flex items-center gap-0.5 text-[11px] text-slate-400">
              <MdOutlineImage size={13} /> {item.photoCount}
            </span>
          ) : null}
        </span>

        <span className="mt-1 line-clamp-2 block break-words text-sm font-semibold text-slate-800">
          {item.message}
        </span>

        <span className="mt-0.5 block break-words text-[11px] text-slate-500 sm:truncate">
          {item.company?.name || "Conta removida"}
          {item.professional?.name ? ` · ${item.professional.name}` : ""}
          {` · ${formatDate(item.createdAt)}`}
        </span>

        {/* Sem a coluna da direita (só cabe de `sm` em diante), o plano e a
            demanda descem para baixo do texto: "N pedidos" é o motivo de a
            lista existir e não pode sumir no celular. */}
        <span className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 sm:hidden">
          {item.company?.planId ? (
            <Badge tone={PLAN_TONE[item.company.planId] || "neutral"}>
              {item.company.planId}
            </Badge>
          ) : null}
          <span className={`text-xs ${demandClass}`}>{demand}</span>
        </span>
      </span>

      <span className="hidden shrink-0 flex-col items-end gap-1 sm:flex">
        {item.company?.planId ? (
          <Badge tone={PLAN_TONE[item.company.planId] || "neutral"}>
            {item.company.planId}
          </Badge>
        ) : null}
        <span className={`text-xs ${demandClass}`}>{demand}</span>
      </span>
    </button>
  );
};

const SuggestionsPage = () => {
  const [status, setStatus] = useState("");
  const [kind, setKind] = useState("");
  const [area, setArea] = useState("");
  const [planId, setPlanId] = useState("");
  const [sort, setSort] = useState("recent");
  const [term, setTerm] = useState("");
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);

  const search = useDebouncedValue(term, 400);

  const filters = { status, kind, area, planId, sort, search: search.trim(), page };

  const query = useQuery(
    ["suggestions", "list", filters],
    async () => {
      const params = new URLSearchParams({
        itemsPerPage: String(PAGE_SIZE),
        sort,
        page: String(page),
      });
      if (status) params.set("status", status);
      if (kind) params.set("kind", kind);
      if (area) params.set("area", area);
      if (planId) params.set("planId", planId);
      if (filters.search) params.set("search", filters.search);

      const { data } = await axios.get(`${SUGGESTIONS.LIST}?${params.toString()}`);
      return data.data;
    },
    { keepPreviousData: true }
  );

  const data = query.data;
  const summary = data?.summary;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  // Mudar qualquer filtro volta para a primeira página: a atual pode nem existir
  // mais no novo recorte.
  const change = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const toggleStatus = (value) => change(setStatus)(status === value ? "" : value);

  const hasFilters = Boolean(status || kind || area || planId || filters.search);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {SUGGESTION_STATUS_ORDER.map((value) => (
          <StatusTile
            key={value}
            label={SUGGESTION_STATUS_META[value].label}
            tone={SUGGESTION_STATUS_META[value].tone}
            value={summary ? summary[SUMMARY_KEY[value]] : 0}
            active={status === value}
            onClick={() => toggleStatus(value)}
          />
        ))}
      </div>

      <Card className="p-3">
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
          <input
            value={term}
            onChange={(event) => change(setTerm)(event.target.value)}
            placeholder="Buscar por texto ou salão"
            className={`${inputClass} col-span-2 sm:min-w-[220px] sm:flex-1`}
          />
          <select
            value={kind}
            onChange={(event) => change(setKind)(event.target.value)}
            className={inputClass}
          >
            <option value="">Todos os tipos</option>
            {Object.entries(SUGGESTION_KIND_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={area}
            onChange={(event) => change(setArea)(event.target.value)}
            className={inputClass}
          >
            <option value="">Todas as abas</option>
            {Object.entries(SUGGESTION_AREA_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={planId}
            onChange={(event) => change(setPlanId)(event.target.value)}
            className={inputClass}
          >
            <option value="">Todos os planos</option>
            {PLAN_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
          <select
            value={sort}
            onChange={(event) => change(setSort)(event.target.value)}
            className={inputClass}
          >
            {SUGGESTION_SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </Card>

      {query.isLoading ? (
        <SkeletonRows rows={6} />
      ) : query.error ? (
        <ErrorBlock
          message={
            query.error.response?.data?.message ||
            "Não foi possível carregar as sugestões."
          }
          onRetry={query.refetch}
        />
      ) : (
        <Card className="overflow-hidden">
          {data.suggestions.length ? (
            data.suggestions.map((item) => (
              <SuggestionRow
                key={item.id}
                item={item}
                onOpen={setOpenId}
                isActive={item.id === openId}
              />
            ))
          ) : (
            <EmptyState
              icon={MdOutlineLightbulb}
              title={
                hasFilters
                  ? "Nenhuma sugestão neste filtro"
                  : "Ainda não chegou nenhuma sugestão"
              }
              description={
                hasFilters
                  ? "Tire algum filtro para ver as demais."
                  : "Quando alguém mandar uma ideia pelo menu do app, ela aparece aqui."
              }
            />
          )}

          {data.total > PAGE_SIZE ? (
            <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-4 py-2.5">
              <span className="min-w-0 text-xs text-slate-500">
                {formatNumber(data.total)} pedidos · página {page} de {totalPages}
              </span>
              <span className="flex shrink-0 gap-1">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-30 sm:p-1.5"
                  aria-label="Página anterior"
                >
                  <MdOutlineChevronLeft size={20} />
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                  className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-30 sm:p-1.5"
                  aria-label="Próxima página"
                >
                  <MdOutlineChevronRight size={20} />
                </button>
              </span>
            </div>
          ) : null}
        </Card>
      )}

      {openId ? (
        <SuggestionDrawer
          suggestionId={openId}
          onClose={() => setOpenId(null)}
          onOpen={setOpenId}
        />
      ) : null}
    </div>
  );
};

export default SuggestionsPage;
